<template>
  <div class="flex w-full" style="position: relative">
    <div v-if="gliders.length" class="d-flex flex-col w-full h-full">
      <div class="flex-grow-0 w-full">
        <v-tabs v-if="showTabs" v-model="selectedGliderIdx" align-with-title class="flex-grow-0">
          <v-tab>
            <v-icon class="mr-2 !text-black">{{ fleetIco }}</v-icon>
            {{ fleet.FLE_LIBELLE }}
          </v-tab>
          <v-tab v-for="(glider, idx) in gliders" :key="glider.id" :title="glider.libelle">
            {{ glider.libelle }}
          </v-tab>
        </v-tabs>
      </div>
      <div class="flex flex-col !p-2 gap-2" v-if="selectedGliderIdx == 0">
        <div class="bg-white !p-4">
          <span class="subheading">{{ $t('Pilot.SendMail.GliderAlarm') }}</span>
          <div class="flex">
            <v-text-field
              :label="$t('Pilot.SendMail.GliderAlarm')"
              v-model="new_mails_glider_alr"
              style="flex: 1 0 auto"
            />
            <v-btn
              :loading="alrSavingCmd"
              :disabled="alrSavingCmd"
              small
              color="primary"
              @click.prevent="addNewMail('Alr')"
            > 
              <v-icon small>mdi-plus</v-icon>
              {{ $t('Fleet.AddToAll') }}
            </v-btn>
          </div>
        </div>

        <div class="bg-white !p-4">
          <span class="subheading">{{ $t('Pilot.SendMail.SurfacingAlarm') }}</span>
          <div class="flex">
            <v-text-field
              :label="$t('Pilot.SendMail.SurfacingAlarm')"
              v-model="new_mails_surfacing"
              style="flex: 1 0 auto"
            />
            <v-btn
              :loading="surfacingSavingCmd"
              :disabled="surfacingSavingCmd"
              small
              color="primary"
              @click.prevent="addNewMail('Surfacing')"
            >
              <v-icon small>mdi-plus</v-icon>
              {{ $t('Fleet.AddToAll') }}
            </v-btn>
          </div>
        </div>

        <div class="bg-white !p-4" v-if="showDetection">
          <span class="subheading">{{ $t('Pilot.SendMail.DetectionAlarm') }}</span>
          <div class="flex">
            <v-text-field
              :label="$t('Pilot.SendMail.DetectionAlarm')"
              v-model="new_mails_detection"
              style="flex: 1 0 auto"
            />
            <v-btn
              :loading="detectionSavingCmd"
              :disabled="detectionSavingCmd"
              small
              color="primary"
              @click.prevent="addNewMail('Detection')"
            >
              <v-icon small>mdi-plus</v-icon>
              {{ $t('Fleet.AddToAll') }}
            </v-btn>
          </div>
        </div>
      </div>
      <mission-detail-send-mail
        v-else
        :uiid="uiid"
        :isleft="isleft"
        :mission="store.myGetters.missionsById[gliders[selectedGliderIdx - 1].id]"
        :isVisible="props.isVisible"
        :key="selectedGliderIdx"
      />
    </div>
    <div v-else class="w-full h-full flex flex-col mx-4 items-center justify-center title gap-2">
      <span>{{ $t('Fleet.NoGliders') }}</span>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, computed, nextTick, onMounted } from 'vue'
import store from '@/store'
import { AloDbFleet, isAloDbFleetCoverage, isAloDbFleetFormation } from '@/models/alodb/AloDbFleet'
import MissionDetailSendMail from '@/components/mission/MissionDetailSendMail.vue'

const new_mails_glider_alr = ref('')
const new_mails_iris_alr = ref('')
const new_mails_surfacing = ref('')
const new_mails_detection = ref('')

const alrSavingCmd = ref(false)
const surfacingSavingCmd = ref(false)
const detectionSavingCmd = ref(false)

const props = defineProps<{
  fleet: AloDbFleet
  isleft: boolean
  uiid: string
  isVisible: boolean
}>()

const fleetIco = computed(() => {
  return isAloDbFleetCoverage(props.fleet)
    ? 'mdi-select-group'
    : isAloDbFleetFormation(props.fleet)
    ? 'mdi-vector-circle'
    : 'mdi-multicast'
})

const showDetection = computed(() => {
  return store.myGetters?.userProfile?.config?.postprocess_image ?? false
})

const missions = computed(
  () => props.fleet.activeMissionIds?.map(id => store.myGetters?.missionsById[id])?.filter(m => !!m) ?? [],
)

const gliders = computed(() => missions.value.map(m => ({ id: m.misId, libelle: m.gliKey })))

// Vuetify VTabs calcule `isOverflowing` (→ flèche de pagination) au montage, à un
// moment où le panneau n'a parfois pas encore sa largeur finale → flèche absente,
// sous-onglets non navigables. Pattern projet (cf FleetDetailNextSurfacingDo,
// MissionPlan, etc.) : remonter le <v-tabs> peu après le mount (showTabs false→true)
// pour forcer une mesure correcte une fois le layout stabilisé.
const showTabs = ref(true)
onMounted(() => {
  setTimeout(() => {
    showTabs.value = false
    nextTick(() => {
      showTabs.value = true
    })
  }, 100)
})

async function addNewMail(destination: 'Alr' | 'Surfacing' | 'Detection') {
  if (
    (destination === 'Alr' && alrSavingCmd.value) ||
    (destination === 'Surfacing' && surfacingSavingCmd.value) ||
    (destination === 'Detection' && detectionSavingCmd.value)
  )
    return
  if (destination === 'Alr') alrSavingCmd.value = true
  else if (destination === 'Detection') detectionSavingCmd.value = true
  else surfacingSavingCmd.value = true

  const mail =
    destination === 'Alr'
      ? new_mails_glider_alr.value
      : destination === 'Detection'
      ? new_mails_detection.value
      : new_mails_surfacing.value
  if (mail.length > 0) {
    for (const mission of missions.value.filter(m => m.misActifOn)) {
      if (mission.misId == null) return
      try {
        await store.myActions.MISSION_IRIS_SEND_MAIL_REQUEST(mission.misId!)
        const SendMail_mailling = store.myGetters.modifiedSendMailConfigs[mission.misId!].mod_settings.SendMail_mailling
        if (!SendMail_mailling.mails_detection) SendMail_mailling.mails_detection = []
        if (!SendMail_mailling.mails_glider_alr) SendMail_mailling.mails_glider_alr = []
        if (!SendMail_mailling.mails_surfacing) SendMail_mailling.mails_surfacing = []
        const mailArr =
          destination === 'Alr'
            ? SendMail_mailling.mails_glider_alr
            : destination === 'Detection'
            ? SendMail_mailling.mails_detection
            : SendMail_mailling.mails_surfacing
        if (!mailArr.includes(mail)) {
          mailArr.push(mail)
          await store.myActions.MISSION_IRIS_SEND_MAIL_SAVE_REQUEST(mission.misId!)
        }
      } catch (e) {
        console.log(e)
      }
    }
    nextTick(() => {
      if (destination === 'Alr') new_mails_glider_alr.value = ''
      else if (destination == 'Detection') new_mails_detection.value = ''
      else new_mails_surfacing.value = ''
      if (destination === 'Alr') alrSavingCmd.value = false
      else if (destination == 'Detection') detectionSavingCmd.value = false
      else surfacingSavingCmd.value = false
    })
  }
}

const selectedGliderIdx = ref(0)
</script>
